import React, { useState, useRef } from 'react';
import { SaveIcon, XIcon, FileTextIcon, AlertTriangleIcon, CheckIcon, UploadIcon } from 'lucide-react';
import { ExchangeRate, ExchangeRateType, EntityScope } from '../CurrencyExchangeView';
interface ExchangeRateUploadProps {
  onImport: (rates: ExchangeRate[]) => void;
  onCancel: () => void;
  locale: string;
}
export function ExchangeRateUpload({
  onImport,
  onCancel,
  locale
}: ExchangeRateUploadProps) {
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [mappings, setMappings] = useState({
    fromCurrency: '',
    toCurrency: '',
    rate: '',
    type: '',
    scope: '',
    isActive: ''
  });
  const [availableColumns, setAvailableColumns] = useState<string[]>([]);
  const [previewData, setPreviewData] = useState<ExchangeRate[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [step, setStep] = useState<'upload' | 'map' | 'preview'>('upload');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    setFile(selectedFile);
    // Check file type
    const fileType = selectedFile.type;
    if (fileType !== 'text/csv' && fileType !== 'application/vnd.ms-excel' && !fileType.includes('spreadsheetml')) {
      setErrors(['Unsupported file format. Please upload a CSV or Excel file.']);
      return;
    }
    // Store document type information with the file
    const fileWithType = {
      file: selectedFile,
      documentType: 'Exchange Rate Data',
      name: selectedFile.name,
      size: selectedFile.size,
      uploadDate: new Date().toISOString()
    };
    // For this demo, we'll just parse CSV files
    // In a real application, you would use a library like xlsx for Excel files
    if (fileType === 'text/csv') {
      const reader = new FileReader();
      reader.onload = event => {
        try {
          const csvData = event.target?.result as string;
          const lines = csvData.split('\n');
          const headers = lines[0].split(',').map(header => header.trim());
          setAvailableColumns(headers);
          const data = [];
          for (let i = 1; i < lines.length; i++) {
            if (lines[i].trim() === '') continue;
            const values = lines[i].split(',').map(value => value.trim());
            const row = {};
            headers.forEach((header, index) => {
              row[header] = values[index];
            });
            data.push(row);
          }
          setParsedData(data);
          setStep('map');
          setErrors([]);
        } catch (error) {
          setErrors(['Failed to parse CSV file. Please check the format.']);
        }
      };
      reader.readAsText(selectedFile);
    } else {
      // For demo purposes, we'll show a message for Excel files
      setErrors(['Excel file parsing is not implemented in this demo. Please use CSV format.']);
    }
  };
  const handleMappingChange = (field: string, column: string) => {
    setMappings({
      ...mappings,
      [field]: column
    });
  };
  const validateAndPreview = () => {
    // Validate required mappings
    if (!mappings.fromCurrency || !mappings.toCurrency || !mappings.rate) {
      setErrors(['From Currency, To Currency, and Rate mappings are required.']);
      return;
    }
    try {
      // Convert parsed data to ExchangeRate format
      const convertedData: ExchangeRate[] = parsedData.map((row, index) => {
        const rateValue = parseFloat(row[mappings.rate]);
        if (isNaN(rateValue)) {
          throw new Error(`Invalid rate value at row ${index + 1}: ${row[mappings.rate]}`);
        }
        return {
          id: `import-${Date.now()}-${index}`,
          fromCurrency: row[mappings.fromCurrency],
          toCurrency: row[mappings.toCurrency],
          rate: rateValue,
          inverseRate: 1 / rateValue,
          type: mappings.type && row[mappings.type] ? row[mappings.type] as ExchangeRateType : 'Manual',
          scope: mappings.scope && row[mappings.scope] ? row[mappings.scope] as EntityScope : 'Global',
          isActive: mappings.isActive ? row[mappings.isActive] === 'true' || row[mappings.isActive] === 'TRUE' || row[mappings.isActive] === '1' : true,
          lastUpdated: new Date().toISOString(),
          updatedBy: 'Import'
        };
      });
      // Validate data
      const validationErrors: string[] = [];
      convertedData.forEach((rate, index) => {
        if (!rate.fromCurrency) {
          validationErrors.push(`Row ${index + 1}: Missing From Currency`);
        }
        if (!rate.toCurrency) {
          validationErrors.push(`Row ${index + 1}: Missing To Currency`);
        }
        if (isNaN(rate.rate) || rate.rate <= 0) {
          validationErrors.push(`Row ${index + 1}: Invalid Rate value`);
        }
      });
      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        return;
      }
      setPreviewData(convertedData);
      setStep('preview');
      setErrors([]);
    } catch (error) {
      setErrors([error.message || 'Failed to process data. Please check your mappings.']);
    }
  };
  const handleImport = () => {
    onImport(previewData);
  };
  return <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-10013">
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-10014">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-10015">
          Import Exchange Rates
        </h3>
        <button onClick={onCancel} className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10016">
          <XIcon className="h-5 w-5" data-id="element-10017" />
        </button>
      </div>
      <div className="p-6 space-y-6" data-id="element-10018">
        {/* Step indicator */}
        <div className="flex items-center justify-center mb-6" data-id="element-10019">
          <div className={`flex items-center ${step === 'upload' ? 'text-indigo-600' : 'text-gray-500'}`} data-id="element-10020">
            <div className={`flex items-center justify-center h-8 w-8 rounded-full ${step === 'upload' ? 'bg-indigo-100' : 'bg-gray-100'}`} data-id="element-10021">
              <UploadIcon className="h-4 w-4" data-id="element-10022" />
            </div>
            <span className="ml-2 text-sm font-medium" data-id="element-10023">Upload File</span>
          </div>
          <div className="h-1 w-8 bg-gray-200 mx-2" data-id="element-10024"></div>
          <div className={`flex items-center ${step === 'map' ? 'text-indigo-600' : 'text-gray-500'}`} data-id="element-10025">
            <div className={`flex items-center justify-center h-8 w-8 rounded-full ${step === 'map' ? 'bg-indigo-100' : 'bg-gray-100'}`} data-id="element-10026">
              <FileTextIcon className="h-4 w-4" data-id="element-10027" />
            </div>
            <span className="ml-2 text-sm font-medium" data-id="element-10028">Map Fields</span>
          </div>
          <div className="h-1 w-8 bg-gray-200 mx-2" data-id="element-10029"></div>
          <div className={`flex items-center ${step === 'preview' ? 'text-indigo-600' : 'text-gray-500'}`} data-id="element-10030">
            <div className={`flex items-center justify-center h-8 w-8 rounded-full ${step === 'preview' ? 'bg-indigo-100' : 'bg-gray-100'}`} data-id="element-10031">
              <CheckIcon className="h-4 w-4" data-id="element-10032" />
            </div>
            <span className="ml-2 text-sm font-medium" data-id="element-10033">Preview & Import</span>
          </div>
        </div>
        {/* Error messages */}
        {errors.length > 0 && <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4" data-id="element-10034">
            <div className="flex" data-id="element-10035">
              <div className="flex-shrink-0" data-id="element-10036">
                <AlertTriangleIcon className="h-5 w-5 text-red-400" data-id="element-10037" />
              </div>
              <div className="ml-3" data-id="element-10038">
                <h3 className="text-sm font-medium text-red-800" data-id="element-10039">
                  There{' '}
                  {errors.length === 1 ? 'is an issue' : `are ${errors.length} issues`}{' '}
                  with your data
                </h3>
                <div className="mt-2 text-sm text-red-700" data-id="element-10040">
                  <ul className="list-disc pl-5 space-y-1" data-id="element-10041">
                    {errors.map((error, index) => <li key={index} data-id="element-10042">{error}</li>)}
                  </ul>
                </div>
              </div>
            </div>
          </div>}
        {/* Upload step */}
        {step === 'upload' && <div data-id="element-10043">
            <div className="flex items-center justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md" data-id="element-10044">
              <div className="space-y-1 text-center" data-id="element-10045">
                <FileTextIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-10046" />
                <div className="flex text-sm text-gray-600" data-id="element-10047">
                  <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500" data-id="element-10048">
                    <span data-id="element-10049">Upload a file</span>
                    <input id="file-upload" name="file-upload" type="file" className="sr-only" ref={fileInputRef} onChange={handleFileUpload} accept=".csv,.xls,.xlsx" data-id="element-10050" />
                  </label>
                  <p className="pl-1" data-id="element-10051">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500" data-id="element-10052">
                  CSV or Excel file with exchange rate data
                </p>
              </div>
            </div>
            <div className="mt-6" data-id="element-10053">
              <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-10054">
                Expected format:
              </h4>
              <div className="bg-gray-50 p-4 rounded-md overflow-x-auto" data-id="element-10055">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-10056">
                  <thead data-id="element-10057">
                    <tr data-id="element-10058">
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10059">
                        From Currency
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10060">
                        To Currency
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10061">
                        Rate
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10062">
                        Type (Optional)
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10063">
                        Scope (Optional)
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10064">
                        Is Active (Optional)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-10065">
                    <tr data-id="element-10066">
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-10067">
                        USD
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-10068">
                        EUR
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-10069">
                        0.92
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-10070">
                        Manual
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-10071">
                        Global
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-10072">
                        TRUE
                      </td>
                    </tr>
                    <tr data-id="element-10073">
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-10074">
                        EUR
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-10075">
                        GBP
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-10076">
                        0.87
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-10077">
                        Manual
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-10078">
                        Global
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-10079">
                        TRUE
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-xs text-gray-500" data-id="element-10080">
                * Header names in your file can be different - you'll map them
                in the next step
              </p>
            </div>
          </div>}
        {/* Mapping step */}
        {step === 'map' && <div data-id="element-10081">
            <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-10082">
              Map your file columns to exchange rate fields
            </h4>
            <div className="space-y-4" data-id="element-10083">
              <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2" data-id="element-10084">
                <div data-id="element-10085">
                  <label htmlFor="fromCurrency" className="block text-sm font-medium text-gray-700" data-id="element-10086">
                    From Currency *
                  </label>
                  <select id="fromCurrency" value={mappings.fromCurrency} onChange={e => handleMappingChange('fromCurrency', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" required data-id="element-10087">
                    <option value="" data-id="element-10088">Select column</option>
                    {availableColumns.map(column => <option key={column} value={column} data-id="element-10089">
                        {column}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-10090">
                  <label htmlFor="toCurrency" className="block text-sm font-medium text-gray-700" data-id="element-10091">
                    To Currency *
                  </label>
                  <select id="toCurrency" value={mappings.toCurrency} onChange={e => handleMappingChange('toCurrency', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" required data-id="element-10092">
                    <option value="" data-id="element-10093">Select column</option>
                    {availableColumns.map(column => <option key={column} value={column} data-id="element-10094">
                        {column}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-10095">
                  <label htmlFor="rate" className="block text-sm font-medium text-gray-700" data-id="element-10096">
                    Rate *
                  </label>
                  <select id="rate" value={mappings.rate} onChange={e => handleMappingChange('rate', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" required data-id="element-10097">
                    <option value="" data-id="element-10098">Select column</option>
                    {availableColumns.map(column => <option key={column} value={column} data-id="element-10099">
                        {column}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-10100">
                  <label htmlFor="type" className="block text-sm font-medium text-gray-700" data-id="element-10101">
                    Type (Optional)
                  </label>
                  <select id="type" value={mappings.type} onChange={e => handleMappingChange('type', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-10102">
                    <option value="" data-id="element-10103">Select column</option>
                    {availableColumns.map(column => <option key={column} value={column} data-id="element-10104">
                        {column}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-10105">
                  <label htmlFor="scope" className="block text-sm font-medium text-gray-700" data-id="element-10106">
                    Scope (Optional)
                  </label>
                  <select id="scope" value={mappings.scope} onChange={e => handleMappingChange('scope', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-10107">
                    <option value="" data-id="element-10108">Select column</option>
                    {availableColumns.map(column => <option key={column} value={column} data-id="element-10109">
                        {column}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-10110">
                  <label htmlFor="isActive" className="block text-sm font-medium text-gray-700" data-id="element-10111">
                    Is Active (Optional)
                  </label>
                  <select id="isActive" value={mappings.isActive} onChange={e => handleMappingChange('isActive', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-10112">
                    <option value="" data-id="element-10113">Select column</option>
                    {availableColumns.map(column => <option key={column} value={column} data-id="element-10114">
                        {column}
                      </option>)}
                  </select>
                </div>
              </div>
              {parsedData.length > 0 && <div className="mt-6" data-id="element-10115">
                  <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-10116">
                    Data preview (first 5 rows):
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-md overflow-x-auto" data-id="element-10117">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-10118">
                      <thead data-id="element-10119">
                        <tr data-id="element-10120">
                          {availableColumns.map(column => <th key={column} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10121">
                              {column}
                            </th>)}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-10122">
                        {parsedData.slice(0, 5).map((row, rowIndex) => <tr key={rowIndex} data-id="element-10123">
                            {availableColumns.map(column => <td key={column} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-10124">
                                {row[column]}
                              </td>)}
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-2 text-xs text-gray-500" data-id="element-10125">
                    Showing {Math.min(5, parsedData.length)} of{' '}
                    {parsedData.length} rows
                  </p>
                </div>}
            </div>
          </div>}
        {/* Preview step */}
        {step === 'preview' && <div data-id="element-10126">
            <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-10127">
              Review and confirm import ({previewData.length} exchange rates)
            </h4>
            <div className="bg-gray-50 p-4 rounded-md overflow-x-auto" data-id="element-10128">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-10129">
                <thead className="bg-gray-50" data-id="element-10130">
                  <tr data-id="element-10131">
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10132">
                      From
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10133">
                      To
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10134">
                      Rate
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10135">
                      Inverse
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10136">
                      Type
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10137">
                      Scope
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10138">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-10139">
                  {previewData.slice(0, 10).map((rate, index) => <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-id="element-10140">
                      <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-10141">
                        {rate.fromCurrency}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-10142">
                        {rate.toCurrency}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-10143">
                        {rate.rate.toFixed(4)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-10144">
                        {rate.inverseRate.toFixed(4)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-10145">
                        {rate.type}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-10146">
                        {rate.scope}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-10147">
                        {rate.isActive ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-10148">
                            Active
                          </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-10149">
                            Inactive
                          </span>}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            {previewData.length > 10 && <p className="mt-2 text-xs text-gray-500" data-id="element-10150">
                Showing 10 of {previewData.length} exchange rates
              </p>}
            <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4" data-id="element-10151">
              <div className="flex" data-id="element-10152">
                <div className="flex-shrink-0" data-id="element-10153">
                  <AlertTriangleIcon className="h-5 w-5 text-yellow-400" data-id="element-10154" />
                </div>
                <div className="ml-3" data-id="element-10155">
                  <p className="text-sm text-yellow-700" data-id="element-10156">
                    Importing these rates will update any existing rates with
                    the same currency pairs. This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
          </div>}
        {/* Action buttons */}
        <div className="pt-5 border-t border-gray-200" data-id="element-10157">
          <div className="flex justify-end space-x-3" data-id="element-10158">
            {step !== 'upload' && <button type="button" onClick={() => setStep(step === 'preview' ? 'map' : 'upload')} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10159">
                Back
              </button>}
            <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10160">
              Cancel
            </button>
            {step === 'upload' && file && <button type="button" onClick={() => fileInputRef.current?.click()} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10161">
                Change File
              </button>}
            {step === 'map' && <button type="button" onClick={validateAndPreview} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10162">
                <CheckIcon className="h-4 w-4 mr-2" data-id="element-10163" />
                Continue
              </button>}
            {step === 'preview' && <button type="button" onClick={handleImport} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10164">
                <SaveIcon className="h-4 w-4 mr-2" data-id="element-10165" />
                Import Rates
              </button>}
          </div>
        </div>
      </div>
    </div>;
}